import { Link } from 'react-router-dom';
import { Laptop, Truck, Shield, Star, Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube, ArrowUpRight } from 'lucide-react';

const FOOTER_LINKS = {
  products: {
    use: [
      { label: 'All Laptops', href: '/products' },
      { label: 'Business Laptops', href: '/products?category=laptop&tag=business' },
      { label: 'Gaming Laptops', href: '/products?category=laptop&tag=gaming' },
      { label: 'Student Laptops', href: '/products?category=laptop&tag=student' },
    ],
    brand: [
      { label: 'MacBook Rentals', href: '/products?brand=Apple' },
      { label: 'HP Rentals', href: '/products?brand=HP' },
      { label: 'Dell Rentals', href: '/products?brand=Dell' },
      { label: 'Lenovo Rentals', href: '/products?brand=Lenovo' },
      { label: 'Samsung Rentals', href: '/products?brand=Samsung' },
      { label: 'MSI Rentals', href: '/products?brand=MSI' },
      { label: 'ASUS Rentals', href: '/products?brand=ASUS' },
      { label: 'Acer Rentals', href: '/products?brand=Acer' },
    ],
  },
  support: [
    { label: 'Product & Pricing', href: '/pricing' },
    { label: 'Book a Laptop', href: '/booking' },
    { label: 'Rental Terms', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Cancellation & Refund', href: '/cancellation-refund-policy' },
    { label: 'Delivery Policy', href: '/delivery-policy' },
    { label: 'Pricing Disclaimer', href: '/pricing-disclaimer' },
    { label: 'Damage Protection', href: '/damage-protection-policy' },
    { label: 'Contact Us', href: '/contact' },
    { label: 'Help & FAQs', href: '/faq' },
  ],
  company: [
    { label: 'About Us', href: '/about' },
    { label: 'All Laptops', href: '/products' },
    { label: 'Categories', href: '/categories' },
    { label: 'Cities', href: '/cities' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact Us', href: '/contact' },
    { label: 'Bulk Enquiry', href: '/contact' },
  ],
  cities: [
    { label: 'Bangalore', href: '/rental/bangalore' },
    { label: 'Mumbai', href: '/rental/mumbai' },
    { label: 'Delhi', href: '/rental/delhi' },
    { label: 'Chennai', href: '/rental/chennai' },
    { label: 'Hyderabad', href: '/rental/hyderabad' },
    { label: 'Pune', href: '/rental/pune' },
    { label: 'Kolkata', href: '/rental/kolkata' },
    { label: 'Ahmedabad', href: '/rental/ahmedabad' },
  ],
};

const SOCIAL_LINKS = [
  { icon: Facebook, href: 'https://facebook.com', label: 'Facebook' },
  { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
  { icon: Instagram, href: 'https://instagram.com', label: 'Instagram' },
  { icon: Linkedin, href: 'https://linkedin.com', label: 'LinkedIn' },
  { icon: Youtube, href: 'https://youtube.com', label: 'YouTube' },
];

const TRUST_BADGES = [
  { icon: Shield, title: 'Damage Protection', desc: 'Comprehensive coverage on all rentals' },
  { icon: Truck, title: 'Free Delivery', desc: 'Doorstep delivery & pickup' },
  { icon: Star, title: 'Quality Assured', desc: 'Certified & tested devices' },
  { icon: Laptop, title: 'Latest Models', desc: 'Newest laptops from top brands' },
];

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-secondary-900 text-secondary-300" role="contentinfo">
      <div className="container py-12 sm:py-16 lg:py-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
          <div className="col-span-1 sm:col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4" aria-label="LaptopRent Home">
              <Laptop className="w-8 h-8 text-primary-400" />
              <span className="font-bold text-xl text-white">LaptopRent</span>
            </Link>
            <p className="text-secondary-400 text-body mb-4 max-w-xs">
              India's trusted laptop rental platform. Rent premium laptops from HP, Dell, Lenovo, Apple, ASUS, Acer, Samsung & MSI with free doorstep delivery.
            </p>
            <div className="mb-6 space-y-1.5 text-sm text-secondary-300">
              <p>Email: <a href="mailto:connect@laptoponrent.online" className="text-white hover:underline font-medium">connect@laptoponrent.online</a></p>
              <p>Customer Care: <a href="tel:+917892240243" className="text-white hover:underline font-medium">+91 78922 40243</a></p>
              <p>WhatsApp: <a href="https://wa.me/917892240243" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline font-medium">+91 78922 40243</a></p>
              <p className="text-xs text-secondary-400">Hours: Monday to Saturday, 10:00 AM &ndash; 7:00 PM</p>
            </div>
            <div className="flex flex-wrap gap-4">
              {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-lg bg-secondary-800 text-secondary-400 hover:bg-secondary-700 hover:text-white transition-colors"
                  aria-label={label}
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <h3 className="font-semibold text-white mb-4">Products</h3>
            <div className="space-y-5">
              <div>
                <h4 className="text-xs font-medium uppercase tracking-wide text-secondary-500 mb-2">By Use Case</h4>
                <ul className="space-y-3" role="list">
                  {FOOTER_LINKS.products.use.map(link => (
                    <li key={link.href}>
                      <Link to={link.href} className="text-secondary-400 hover:text-white transition-colors text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-medium uppercase tracking-wide text-secondary-500 mb-2">By Brand</h4>
                <ul className="grid grid-cols-2 gap-x-6 gap-y-3" role="list">
                  {FOOTER_LINKS.products.brand.map(link => (
                    <li key={link.href}>
                      <Link to={link.href} className="text-secondary-400 hover:text-white transition-colors text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Support</h3>
            <ul className="space-y-3" role="list">
              {FOOTER_LINKS.support.map(link => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-secondary-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Company</h3>
            <ul className="space-y-3" role="list">
              {FOOTER_LINKS.company.map(link => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-secondary-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Cities</h3>
            <ul className="space-y-3" role="list">
              {FOOTER_LINKS.cities.map(link => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-secondary-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 p-6 bg-secondary-800/50 rounded-xl border border-secondary-700">
          {TRUST_BADGES.map((badge, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-900/30 flex items-center justify-center text-primary-400">
                <badge.icon className="w-5 h-5" aria-hidden="true" />
              </div>
              <div>
                <h4 className="font-medium text-white">{badge.title}</h4>
                <p className="text-secondary-400 text-sm">{badge.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Indic Multi-lingual & Voice Search Language Bar */}
        <div className="py-4 px-6 bg-secondary-900/80 rounded-xl border border-secondary-800 text-center mb-6">
          <p className="text-sm font-medium text-white mb-2">
            🇮🇳 भारत की प्रमुख भाषाओं में लैपटॉप रेंटल सेवा देखें (Indic Multi-Lingual &amp; Voice Search):
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
            <a href="/hi/" className="text-primary-400 hover:text-white underline font-medium">हिन्दी (Hindi)</a>
            <span className="text-secondary-600">·</span>
            <a href="/kn/" className="text-primary-400 hover:text-white underline font-medium">ಕನ್ನಡ (Kannada)</a>
            <span className="text-secondary-600">·</span>
            <a href="/ta/" className="text-primary-400 hover:text-white underline font-medium">தமிழ் (Tamil)</a>
            <span className="text-secondary-600">·</span>
            <a href="/te/" className="text-primary-400 hover:text-white underline font-medium">తెలుగు (Telugu)</a>
            <span className="text-secondary-600">·</span>
            <a href="/mr/" className="text-primary-400 hover:text-white underline font-medium">मराठी (Marathi)</a>
            <span className="text-secondary-600">·</span>
            <a href="/bn/" className="text-primary-400 hover:text-white underline font-medium">বাংলা (Bengali)</a>
            <span className="text-secondary-600">·</span>
            <a href="/gu/" className="text-primary-400 hover:text-white underline font-medium">ગુજરાતી (Gujarati)</a>
          </div>
        </div>

        <div className="pt-8 border-t border-secondary-800 space-y-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 text-xs text-secondary-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Rental Terms</Link>
              <Link to="/cancellation-refund-policy" className="hover:text-white transition-colors">Cancellation &amp; Refund</Link>
              <Link to="/delivery-policy" className="hover:text-white transition-colors">Delivery Policy</Link>
              <Link to="/pricing-disclaimer" className="hover:text-white transition-colors">Pricing Disclaimer</Link>
              <Link to="/damage-protection-policy" className="hover:text-white transition-colors">Damage Protection</Link>
            </div>
            <div className="flex items-center gap-2 text-secondary-500 text-xs">
              <span>Made with</span>
              <span className="text-error-500">♥</span>
              <span>in India</span>
            </div>
          </div>

          <div className="w-full text-center text-xs text-secondary-400 space-y-1 pt-4 border-t border-secondary-800/60 leading-relaxed">
            <p>
              laptoponrent.online is a brand of STAGE-X, a technology platform connecting customers with partner laptop rental providers. Laptops are owned and delivered by partner rental companies; bookings, payments, deposits, support and refunds are handled by STAGE-X.
              <br />
              Operating Region: Bengaluru, Karnataka, India
              <br />
              Customer Care: <a href="tel:+917892240243" className="text-secondary-300 hover:underline">+91 78922 40243</a> &middot; Email: <a href="mailto:connect@laptoponrent.online" className="underline text-secondary-300">connect@laptoponrent.online</a> &middot; Hours: Monday to Saturday, 10:00 AM &ndash; 7:00 PM
            </p>
            <p>Payments on this website are processed by STAGE-X.</p>
            <p>&copy; {currentYear} STAGE-X. All rights reserved.</p>
          </div>
        </div>
      </div>

      <a
        href="#root"
        className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-primary-600 text-white flex items-center justify-center shadow-elevated opacity-0 invisible transition-all duration-300 hover:bg-primary-700 hover:scale-105 focus:opacity-100 focus:visible z-40"
        aria-label="Back to top"
      >
        <ArrowUpRight className="w-5 h-5" />
      </a>
    </footer>
  );
}